import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import NodeIcon from "../Applications/NodeIcon";
import { TRIGGERS } from "../Applications/triggers";

const useStyles = makeStyles(theme => ({
  page: { height: "100%", overflowY: "auto", background: theme.app.chatBg },
  wrap: { width: "100%", maxWidth: 1200, margin: "0 auto", padding: theme.spacing(3.5, 3, 6) },
  header: { marginBottom: theme.spacing(2.5) },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 22, color: theme.app.ink },
  subtitle: { color: theme.app.textTertiary, fontSize: 13, marginTop: 4 },
  filters: { display: "flex", gap: 10, marginBottom: theme.spacing(2.5) },
  search: { width: 300 },
  cat: { width: 220 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 },
  card: {
    background: "#fff",
    border: `1px solid ${theme.app.border}`,
    borderRadius: 10,
    padding: "16px 16px 13px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    minHeight: 168,
    transition: "border-color .12s, box-shadow .12s",
    "&:hover": { borderColor: theme.app.textTertiary, boxShadow: "0 6px 18px rgba(20,20,20,.06)" },
  },
  icons: { display: "flex", gap: 6, marginBottom: 12, minHeight: 28 },
  cTitle: { fontWeight: 600, fontSize: 15, color: theme.app.ink, marginBottom: 6 },
  cDesc: {
    fontSize: 12.5,
    color: theme.app.textSecondary,
    lineHeight: 1.5,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
  },
  foot: { display: "flex", alignItems: "center", gap: 6, marginTop: "auto", paddingTop: 12, flexWrap: "wrap" },
  tag: { fontFamily: theme.app.mono, fontSize: 10.5, padding: "2px 7px", borderRadius: 5, background: theme.app.lineSoft, color: theme.app.textSecondary },
  trig: { fontFamily: theme.app.mono, fontSize: 10.5, padding: "2px 7px", borderRadius: 5, marginLeft: "auto" },
  empty: { padding: theme.spacing(8, 2), textAlign: "center", color: theme.app.textTertiary, fontSize: 13 },
}));

const Templates = () => {
  const classes = useStyles();
  const history = useHistory();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/templates");
      setTemplates(data.templates || []);
      setCategories(data.categories || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const query = q.trim().toLowerCase();
  const visible = templates.filter(t => {
    if (cat !== "all" && (t.category || "") !== cat) return false;
    if (!query) return true;
    return (
      (t.name || "").toLowerCase().includes(query) ||
      (t.description || "").toLowerCase().includes(query) ||
      (t.tags || []).some(g => g.toLowerCase().includes(query))
    );
  });

  const trigStyle = tt => {
    const dir = (TRIGGERS[tt] || {}).direction;
    if (dir === "inbound") return { color: "#2F6FB0", background: "#EAF1F8" };
    return { color: "#6A52C4", background: "#F0EDFA" };
  };

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <div className={classes.header}>
          <Typography className={classes.title}>Templates</Typography>
          <Typography className={classes.subtitle}>
            Fluxos prontos pra usar. Abra um, visualize e faça deploy na sua conta em segundos.
          </Typography>
        </div>

        <div className={classes.filters}>
          <TextField
            className={classes.search}
            size="small"
            variant="outlined"
            placeholder="Buscar template…"
            value={q}
            onChange={e => setQ(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
          />
          {categories.length > 0 && (
            <TextField className={classes.cat} select size="small" variant="outlined" label="Caso de uso" value={cat} onChange={e => setCat(e.target.value)}>
              <MenuItem value="all">Todos os casos</MenuItem>
              {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          )}
        </div>

        {visible.length === 0 ? (
          <div className={classes.empty}>Nenhum template disponível ainda.</div>
        ) : (
          <div className={classes.grid}>
            {visible.map(t => (
              <div key={t.id} className={classes.card} onClick={() => history.push(`/templates/${t.id}`)}>
                <div className={classes.icons}>
                  {(t.icons || []).map((ic, i) =>
                    ic.type === "connector" ? (
                      <NodeIcon key={i} brand={ic.connectorType} size={26} />
                    ) : (
                      <NodeIcon key={i} verbKey={ic.verbKey} size={26} color="#5F5E5A" />
                    )
                  )}
                </div>
                <div className={classes.cTitle}>{t.name}</div>
                <div className={classes.cDesc}>{t.description || "Sem descrição."}</div>
                <div className={classes.foot}>
                  {(t.tags || []).slice(0, 3).map(g => <span key={g} className={classes.tag}>{g}</span>)}
                  {t.triggerType && (
                    <span className={classes.trig} style={trigStyle(t.triggerType)}>
                      {(TRIGGERS[t.triggerType] || {}).direction === "inbound" ? "entrada" : "saída"}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Templates;
